/**
 * The app's dropdown. Never a native `<select>`.
 *
 * A native select renders the operating system's own menu — white on macOS whatever
 * the theme, its own type, its own corners — so one appears as a hole punched through
 * the design. The register pickers have always been built this way; the forms had not,
 * and every one added since inherited the hole.
 *
 * Portalled for the reason `lib/Portal.tsx` exists: these open from inside panels that
 * carry a transform, and a transform makes an ancestor the containing block for
 * `position: fixed`, which drops a menu into the middle of a scrolling panel instead of
 * over the page.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Portal } from '../lib/Portal.tsx'

export interface DropdownOption {
  value: string
  label: string
  /**
   * What the trigger says when this one is chosen, where the label alone is ambiguous.
   *
   * Set by `lib/qualify.ts` and absent everywhere it is not needed. The menu shows
   * `label` under its group heading; the trigger stands alone and has to carry the
   * group with it — "Expenses" is three envelopes, and a closed picker showing one
   * word cannot say which.
   */
  qualified?: string | undefined
}

export interface DropdownGroup {
  label: string
  options: DropdownOption[]
}

/** Roughly the tallest the menu gets before it scrolls, for deciding whether to flip. */
const MENU_HEIGHT = 300

/**
 * Above this many options, scrolling to find one is worse than typing it.
 *
 * Eighty envelopes is the case that forced this — a list that long is not a menu, it is
 * a haystack. Short lists keep the plain menu, because a search box over six accounts is
 * a field you have to dismiss before you can point at what you can already see.
 */
const SEARCHABLE_FROM = 12

export function Dropdown({
  value,
  options,
  onChange,
  label,
  placeholder = 'Choose…',
  disabled = false,
}: {
  value: string
  /** Flat, or grouped with headings. */
  options: DropdownOption[] | DropdownGroup[]
  onChange: (value: string) => void
  label: string
  placeholder?: string
  disabled?: boolean
}) {
  const trigger = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [rect, setRect] = useState<DOMRect | null>(null)

  const first = options[0]
  const grouped: DropdownGroup[] =
    first !== undefined && 'options' in first
      ? (options as DropdownGroup[])
      : [{ label: '', options: options as DropdownOption[] }]
  const flat = grouped.flatMap((group) => group.options)
  const chosen = flat.find((option) => option.value === value)

  const [query, setQuery] = useState('')
  const searchable = flat.length >= SEARCHABLE_FROM
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (needle === '') return grouped
    return grouped
      .map((group) => ({
        ...group,
        options: group.options.filter((option) => option.label.toLowerCase().includes(needle)),
      }))
      .filter((group) => group.options.length > 0)
  }, [grouped, query])

  useEffect(() => {
    if (!open) return
    // Escape closes without choosing, like every other overlay here.
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const toggle = () => {
    if (disabled) return
    setRect(trigger.current?.getBoundingClientRect() ?? null)
    // Cleared on open, so the last search is not still filtering the list next time.
    setQuery('')
    setOpen((was) => !was)
  }

  const below = rect !== null && rect.bottom + MENU_HEIGHT <= window.innerHeight

  return (
    <>
      <button
        ref={trigger}
        type="button"
        disabled={disabled}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
        className="flex w-full items-center justify-between gap-2 rounded-[10px] border border-line bg-card px-3 py-2.5 text-left text-[14px] text-ink outline-none transition-colors hover:border-line-strong focus:border-ink disabled:opacity-50"
      >
        <span className={`truncate ${chosen ? '' : 'text-muted'}`}>
          {chosen?.qualified ?? chosen?.label ?? placeholder}
        </span>
        <span aria-hidden className="flex-none text-[10px] text-muted">
          ▾
        </span>
      </button>

      {open && rect && (
        <Portal>
          {/* Transparent rather than dimmed: these sit inside a panel you are reading,
              and darkening it to choose a value reads as leaving the form. */}
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[80] cursor-default bg-transparent"
          />
          <div
            role="listbox"
            aria-label={label}
            data-visible="true"
            style={{
              left: Math.min(rect.left, window.innerWidth - rect.width - 12),
              width: Math.max(rect.width, 220),
              ...(below ? { top: rect.bottom + 4 } : { bottom: window.innerHeight - rect.top + 4 }),
            }}
            className="kete-pop fixed z-[81] flex max-h-[300px] flex-col overflow-y-auto rounded-[12px] border border-line bg-paper py-1 shadow-[var(--shadow-pop)]"
          >
            {searchable && (
              <input
                // biome-ignore lint/a11y/noAutofocus: the menu was opened to pick from
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search"
                aria-label={`Search ${label}`}
                className="mx-1 mb-1 rounded-[8px] border border-line bg-card px-2.5 py-1.5 text-[13px] text-ink outline-none placeholder:text-muted focus:border-ink"
              />
            )}
            {shown.length === 0 && (
              <span className="px-3 py-2 text-[13px] text-muted">Nothing matches “{query}”.</span>
            )}
            {shown.map((group) => (
              <div key={group.label} className="flex flex-col">
                {group.label !== '' && (
                  <span className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.14em] text-muted">
                    {group.label}
                  </span>
                )}
                {group.options.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    onClick={() => {
                      onChange(option.value)
                      setOpen(false)
                    }}
                    className={`px-3 py-2 text-left text-[13.5px] transition-colors hover:bg-sand ${
                      option.value === value ? 'text-ink' : 'text-ink-dim'
                    }`}
                  >
                    {option.value === value && <span aria-hidden>✓ </span>}
                    {option.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </Portal>
      )}
    </>
  )
}
